import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import ErrorBoundary from 'layout/components/ErrorBoundary';
import { TopBar } from 'topBar/components/TopBar';
import { useScrolledPast } from 'topBar/hooks/useScrolledPast';
import DashboardFilters from 'dashboard/components/DashboardFilters';
import OverviewSection from 'dashboard/components/OverviewSection';
import EventsSection from 'dashboard/components/EventsSection';
import UrgencySection from 'dashboard/components/UrgencySection/UrgencySection';
import RiskSection from 'dashboard/components/RiskSection/RiskSection';
import BehavioursSection from 'dashboard/components/BehavioursSection/BehavioursSection';
import CompareSection from 'dashboard/components/CompareSection/CompareSection';
import HeatmapSection from 'dashboard/components/HeatmapSection/HeatmapSection';
import { useDashboardFilters } from 'dashboard/hooks/useDashboardFilters';
import { usePosthogCapture } from 'dashboard/posthogCapture/_index';
import routesConfig from 'constants/routesConfig.json';

import * as Text from 'theme/components/Text.style';
import * as Styled from 'dashboard/styles/Dashboard.style';

export default function DashboardPage(): JSX.Element {
  const { t: tSidePanel } = useTranslation('sidePanel');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const isScrolledPast = useScrolledPast(titleRef);
  const location = useLocation<{
    dashboardFilters?: { fromDate: string; toDate: string; siteIds: string[] };
  }>();
  const { filters, setDateRange, setSiteIds, queryParams } =
    useDashboardFilters(location.state?.dashboardFilters);
  const history = useHistory();
  const dashboard = usePosthogCapture();

  const handleEventSelect = useCallback(
    (eventId: string) => {
      dashboard.eventOpened();
      history.replace({
        pathname: routesConfig.INSIGHTS_PAGE,
        state: { dashboardFilters: filters },
      });
      history.push({
        pathname: `${routesConfig.EVENT_PAGE}/${eventId}`,
        state: { dashboardFilters: filters },
      });
    },
    [history, dashboard, filters]
  );

  return (
    <ErrorBoundary>
      <Styled.DashboardPage>
        <TopBar title={tSidePanel('Insights')} showTitle={isScrolledPast} />
        <Styled.DashboardContentContainer>
          <Styled.DashboardContent>
            <Styled.HeaderContainer>
              <Text.H3 ref={titleRef}>{tSidePanel('Insights')}</Text.H3>
              <DashboardFilters
                fromDate={filters.fromDate}
                toDate={filters.toDate}
                siteIds={filters.siteIds}
                onDateRangeChange={setDateRange}
                onSiteIdsChange={setSiteIds}
              />
            </Styled.HeaderContainer>

            <Styled.WidgetGrid>
              <OverviewSection queryParams={queryParams} />
              <UrgencySection queryParams={queryParams} />
              <BehavioursSection queryParams={queryParams} />
              <RiskSection queryParams={queryParams} />
              <HeatmapSection
                queryParams={queryParams}
                fromDate={filters.fromDate}
                toDate={filters.toDate}
              />
              <CompareSection
                queryParams={queryParams}
                siteIds={filters.siteIds}
              />
              <EventsSection
                queryParams={queryParams}
                onEventSelect={handleEventSelect}
              />
            </Styled.WidgetGrid>
          </Styled.DashboardContent>
        </Styled.DashboardContentContainer>
      </Styled.DashboardPage>
    </ErrorBoundary>
  );
}
